import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { formatPrice, formatDate } from "../utils/helpers";
import { salesAPI } from "../services/api";
import ChatModal from "../components/ChatModal";

export default function MySales() {
  const { getMySales, getOrCreateChat, confirmWithCode, currentUser, loadSales } = useApp();
  const [openChatId, setOpenChatId]   = useState(null);
  const [codeInputs, setCodeInputs]   = useState({});
  const [codeErrors, setCodeErrors]   = useState({});
  const [codeLoading, setCodeLoading] = useState({});
  const [refunding, setRefunding]     = useState({});

  if (!currentUser) return null;
  const sales = getMySales();

  const handleContact = async (sale) => {
    const chat = await getOrCreateChat(sale.productId, sale.buyerId);
    if (chat) setOpenChatId(chat.id);
  };

  const handleRefund = async (sale) => {
    if (!window.confirm(`¿Te quedaste sin stock de "${sale.productTitle}"? Se le va a reembolsar el pago completo a ${sale.buyerName} y la venta se cancela.`)) return;
    setRefunding(prev => ({...prev, [sale.id]: true}));
    try {
      await salesAPI.refund(sale.id);
      alert("Listo, se le reembolso la compra completa.");
      loadSales();
    } catch(e) {
      alert("No se pudo reembolsar: " + e.message);
    } finally {
      setRefunding(prev => ({...prev, [sale.id]: false}));
    }
  };

  const handleConfirmCode = async (saleId) => {
    const code = codeInputs[saleId] || "";
    if (!code.trim()) { setCodeErrors(prev => ({...prev, [saleId]: "Ingresa el codigo"})); return; }
    setCodeLoading(prev => ({...prev, [saleId]: true}));
    setCodeErrors(prev => ({...prev, [saleId]: ""}));
    try {
      await confirmWithCode(saleId, code);
    } catch(e) {
      setCodeErrors(prev => ({...prev, [saleId]: e.message}));
    } finally {
      setCodeLoading(prev => ({...prev, [saleId]: false}));
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Mis ventas</h1>
      <p style={s.sub}>Productos que te compraron a vos.</p>

      {sales.length === 0 ? (
        <div style={s.empty}>
          <span style={{fontSize:56}}>🏷️</span>
          <p style={s.emptyTitle}>Todavia no te compraron nada</p>
          <p style={s.emptyText}>Cuando alguien compre uno de tus productos, aparecera aca.</p>
        </div>
      ) : (
        <div style={s.list}>
          {sales.map(sale => (
            <div key={sale.id} style={{...s.card,...(sale.status==="pending"?s.cardNew:{})}}>
              {sale.status === "pending" && <div style={s.newBadge}>🔔 Nueva venta</div>}

              <div style={s.cardTop}>
                <div style={s.cardInfo}>
                  <p style={s.productTitle}>{sale.productTitle}</p>

                  <div style={s.buyerBox}>
                    <span style={{fontSize:22}}>👤</span>
                    <div>
                      <p style={s.buyerLabel}>Comprado por</p>
                      <p style={s.buyerName}>{sale.buyerName}</p>
                    </div>
                  </div>

                  {sale.cantidad > 1 && (
                    <div style={s.cantidadBox}>
                      <span style={{fontSize:18}}>📦</span>
                      <div>
                        <p style={s.buyerLabel}>Cantidad pedida</p>
                        <p style={s.cantidadVal}>{sale.cantidad} unidades</p>
                      </div>
                    </div>
                  )}

                  {sale.shippingAddress && (
                    <div style={s.addressBox}>
                      <span style={{fontSize:18}}>📦</span>
                      <div>
                        <p style={s.buyerLabel}>Enviar a</p>
                        <p style={s.addressVal}>{sale.shippingAddress}{sale.shippingPostalCode ? ` (CP ${sale.shippingPostalCode})` : ""}</p>
                        {sale.shippingCost > 0 && <p style={s.meta}>Envio pago por el comprador: {formatPrice(sale.shippingCost)} (incluido en el total, sin comision)</p>}
                      </div>
                    </div>
                  )}

                  <p style={s.meta}>📅 {formatDate(sale.date)}</p>
                  <p style={s.meta}>💳 Mercado Pago</p>
                </div>

                <div style={s.priceBox}>
                  {sale.cantidad > 1 && <p style={s.priceUnit}>{formatPrice(sale.pricePerUnit)} c/u</p>}
                  <p style={s.priceLabel}>Total venta</p>
                  <p style={s.price}>{formatPrice(sale.price)}</p>
                  <p style={s.priceLabel}>Vos recibis</p>
                  <p style={s.sellerPrice}>{formatPrice(sale.sellerReceives)}</p>
                </div>
              </div>

              <div style={s.actionRow}>
                {sale.status === "pending" ? (
                  <div style={s.deliveryBox}>
                    <div style={s.deliveryInfo}>
                      <p style={s.deliveryTitle}>🔑 Confirmar entrega con codigo</p>
                      <p style={s.deliveryText}>
                        Cuando le entregues el producto a <strong>{sale.buyerName?.split(" ")[0]}</strong>, pedile el codigo de entrega e ingresalo aca.
                      </p>
                    </div>
                    <div style={s.codeInputRow}>
                      <input
                        style={s.codeInput}
                        placeholder="000000"
                        maxLength={6}
                        value={codeInputs[sale.id] || ""}
                        onChange={e => setCodeInputs(prev => ({...prev, [sale.id]: e.target.value.replace(/\D/g,"")}))}
                        onKeyDown={e => e.key === "Enter" && handleConfirmCode(sale.id)}
                      />
                      <button
                        style={{...s.btnConfirm, opacity: codeLoading[sale.id]?0.7:1}}
                        onClick={() => handleConfirmCode(sale.id)}
                        disabled={codeLoading[sale.id]}
                      >
                        {codeLoading[sale.id] ? "..." : "✅ Confirmar"}
                      </button>
                    </div>
                    {codeErrors[sale.id] && <p style={s.codeError}>❌ {codeErrors[sale.id]}</p>}
                    <div style={s.pendingActions}>
                      <button style={s.btnChat} onClick={() => handleContact(sale)}>
                        💬 Contactar a {sale.buyerName?.split(" ")[0]}
                      </button>
                      {sale.paymentStatus === "paid" && (
                        <button style={{...s.btnRefund, opacity: refunding[sale.id]?0.7:1}} onClick={() => handleRefund(sale)} disabled={refunding[sale.id]}>
                          {refunding[sale.id] ? "..." : "📦 Me quede sin stock, reembolsar"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={s.receivedBox}>
                    <div>
                      <p style={s.receivedTitle}>✅ Entrega confirmada</p>
                      <p style={s.receivedText}>{sale.buyerName} confirmo la entrega el {formatDate(sale.receivedDate)}.</p>
                    </div>
                    <button style={{...s.btnChat,background:"#f0f0f0",color:"#333"}} onClick={() => handleContact(sale)}>
                      💬 Ver chat
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {openChatId && <ChatModal chatId={openChatId} onClose={() => setOpenChatId(null)} />}
    </div>
  );
}

const s = {
  page:{maxWidth:800,margin:"0 auto",padding:"24px 16px"},
  heading:{fontSize:24,fontWeight:600,marginBottom:4},
  sub:{fontSize:14,color:"#888",marginBottom:24},
  empty:{textAlign:"center",padding:"80px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:12},
  emptyTitle:{fontSize:18,fontWeight:500,color:"#333"},
  emptyText:{fontSize:14,color:"#888"},
  list:{display:"flex",flexDirection:"column",gap:20},
  card:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:14,padding:"20px",position:"relative",marginTop:8},
  cardNew:{border:"1.5px solid #1D9E75",boxShadow:"0 2px 12px rgba(29,158,117,0.12)"},
  newBadge:{position:"absolute",top:-12,left:20,background:"#1D9E75",color:"#fff",fontSize:12,fontWeight:600,padding:"3px 14px",borderRadius:20},
  cardTop:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,gap:16,flexWrap:"wrap"},
  cardInfo:{flex:1,display:"flex",flexDirection:"column",gap:8,minWidth:200},
  productTitle:{fontSize:16,fontWeight:600,color:"#111"},
  buyerBox:{display:"flex",alignItems:"center",gap:10,background:"#f0faf6",borderRadius:10,padding:"10px 14px"},
  buyerLabel:{fontSize:11,color:"#888",marginBottom:2},
  buyerName:{fontSize:15,fontWeight:600,color:"#0F6E56"},
  cantidadBox:{display:"flex",alignItems:"center",gap:10,background:"#f0f7ff",borderRadius:10,padding:"10px 14px"},
  cantidadVal:{fontSize:15,fontWeight:600,color:"#009ee3"},
  addressBox:{display:"flex",alignItems:"flex-start",gap:10,background:"#fff8e1",borderRadius:10,padding:"10px 14px"},
  addressVal:{fontSize:14,fontWeight:600,color:"#b7830a",lineHeight:1.4},
  meta:{fontSize:13,color:"#666"},
  priceBox:{textAlign:"right",flexShrink:0,display:"flex",flexDirection:"column",gap:2},
  priceUnit:{fontSize:12,color:"#aaa"},
  priceLabel:{fontSize:11,color:"#aaa"},
  price:{fontSize:14,fontWeight:500,color:"#888",marginBottom:4},
  sellerPrice:{fontSize:22,fontWeight:700,color:"#1D9E75"},
  actionRow:{borderTop:"0.5px solid #f0f0f0",paddingTop:16},
  deliveryBox:{display:"flex",flexDirection:"column",gap:12},
  deliveryInfo:{},
  deliveryTitle:{fontSize:14,fontWeight:600,color:"#333",marginBottom:4},
  deliveryText:{fontSize:13,color:"#555",lineHeight:1.5},
  codeInputRow:{display:"flex",gap:10,alignItems:"center"},
  codeInput:{flex:1,padding:"12px 16px",borderRadius:10,border:"2px solid #1D9E75",fontSize:22,fontWeight:700,letterSpacing:6,textAlign:"center",maxWidth:180,color:"#111"},
  btnConfirm:{background:"#1D9E75",color:"#fff",border:"none",borderRadius:10,padding:"12px 20px",fontSize:14,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"},
  codeError:{fontSize:13,color:"#E24B4A",fontWeight:500},
  btnChat:{alignSelf:"flex-start",background:"#f0f0f0",color:"#333",border:"none",borderRadius:10,padding:"10px 18px",fontSize:14,cursor:"pointer"},
  pendingActions:{display:"flex",gap:10,flexWrap:"wrap"},
  btnRefund:{alignSelf:"flex-start",background:"#fff",color:"#c0392b",border:"1px solid #f5c6c6",borderRadius:10,padding:"10px 18px",fontSize:14,cursor:"pointer"},
  receivedBox:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:16,flexWrap:"wrap"},
  receivedTitle:{fontSize:14,fontWeight:600,color:"#0F6E56",marginBottom:4},
  receivedText:{fontSize:13,color:"#555"},
};
